import { MultiAPILink } from '@habx/apollo-multi-endpoint-link';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
  Observable,
} from '@apollo/client';
import type {
  ApolloQueryResult,
  FetchPolicy,
  FetchResult,
  NormalizedCacheObject,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import _ from 'lodash';

import config from 'config';
import { sentryLink } from 'utils/sentryLink';
import { PaginatedEmployees } from 'generated/graphql';

import { isTokenExpired } from './auth/isTokenExpired';
import Api from './api';

function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const authLink = new ApolloLink((operation, forward) => {
    const token = Api.defaultHeaders.Authorization?.split(' ')[1];
    if (Api.LOGGING_OUT) {
      // This is an equivalent of RxJS' `Observable.never()` (notice that this is NOT RxJS' Observable we are dealing with here)
      // Basically, if we are in the process of logging out, interrupt the GraphQL request and let the link hang.
      // If we were to do `Obserable((observer) => observer.complete())`, than Apollo Client would turn it into a rejected promiss, which is not our intention here: we want to interrup it, but not to fail it
      return new Observable(() => {});
    }

    if (token && isTokenExpired(token)) {
      Api.handleFailedAuth();
      // Same as above, hang the GraphQL request if the token is expired, but don't let it fail (not to trigger the error handling logic)
      return new Observable(() => {});
    }

    return forward(operation);
  });

  return new ApolloClient({
    connectToDevTools: process.env.NODE_ENV !== 'production',
    link: ApolloLink.from([
      authLink,
      sentryLink,
      new MultiAPILink({
        endpoints: {
          graph: config.graphApiUrl,
          payroll: config.payrollApiUrl,
          report: config.reportApiUrl,
          nonprofit: config.nonprofitApiUrl,
          loggedTime: config.loggedTimeApiUrl,
          donationBatch: config.donationBatchApiUrl,
          timeOff: config.timeOffApiUrl,
          opportunities: config.opportunitiesUrl,
        },
        httpSuffix: '/',
        createHttpLink: () =>
          createHttpLink({
            fetch: async (uri: string, options: RequestInit): Promise<Response> =>
              fetch(uri, {
                ...options,
                headers: { ...Api.defaultHeaders, ...options.headers },
              }),
          }),
      }),
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        // NotificationSettings are a singleton, they don't need an id
        NotificationSettings: {
          keyFields: [],
        },
        Query: {
          fields: {
            employees: {
              // Don't merge employees search with different "search" arg
              keyArgs: ['search'],
              merge: (
                existing: PaginatedEmployees | undefined,
                incoming: PaginatedEmployees
              ): PaginatedEmployees => ({
                ...existing,
                ...incoming,
                users: _.uniqBy([...(existing?.users || []), ...(incoming?.users || [])], '__ref'),
              }),
            },
          },
        },
      },
    }),
  });
}

/**
 * It's safe to treat Apollo Client as a singleton, as the auth taken is stored on
 * Api.defaultHeaders and accessed on each new request via `createHttpLink`
 */
export const apolloClient = createApolloClient();

const interceptUnauthorized = <T, R extends ApolloQueryResult<T> | FetchResult<T>>(
  response: R
): R => {
  if (response?.errors) {
    if (
      response?.errors.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error: any) =>
          error.message === 'Not Authorised!' ||
          error.message === 'Error: Unauthorized' ||
          error.message === 'jwt expired'
      )
    ) {
      if (response?.status === 401) {
        Api.handleFailedAuth();
      }
      throw new Error('Not Authorized!');
    }
    throw new Error(JSON.stringify(response.errors));
  }
  return response;
};

export const query = async <T, Tvariables = OperationVariables>(
  document: TypedDocumentNode<T, Tvariables>,
  variables?: Tvariables,
  fetchPolicy?: FetchPolicy
): Promise<ApolloQueryResult<T>> =>
  apolloClient
    .query<T, Tvariables>({ query: document, variables, fetchPolicy })
    .then(interceptUnauthorized);

export const mutate = async <T, Tvariables = OperationVariables>(
  document: TypedDocumentNode<Tvariables, T>,
  variables?: Tvariables,
  refetchQueries?: () => Array<any>
): Promise<FetchResult<T>> =>
  apolloClient
    .mutate<T, Tvariables>({
      mutation: document,
      variables,
      ...(refetchQueries ? { refetchQueries } : {}),
    })
    .then(interceptUnauthorized);
